import {
  ADMIN_EXCLUSIVE_CORE_SUBJECTS,
  MANAGEMENT_CORE_SUBJECTS,
  MANAGER_EXTRA_CORE_PERMISSIONS,
  MANAGER_VIEW_ONLY_CORE_SUBJECTS,
  MEMBER_EXTRA_CORE_PERMISSIONS,
  SALES_CORE_SUBJECTS,
  SUPERVISION_CORE_SUBJECTS,
} from './catalogs/core';
import { PLATFORM_MODULE } from './catalogs/platform';
import type { PermissionId } from './permission-id';
import type { PermissionRegistry } from './registry';

export const ROLE_PRESET_IDS = ['admin', 'manager', 'member', 'platform-admin'] as const;

export type RolePresetId = (typeof ROLE_PRESET_IDS)[number];

/**
 * Where a member works (spec 044): `sales`, `service` or `both`. Orthogonal to the role, which
 * says how much they may do. A `service` member gets nothing of the sales subjects, whatever
 * the role; `sales` takes nothing away (a seller also answers conversations).
 */
export const MEMBERSHIP_AREAS = ['sales', 'service', 'both'] as const;

export type MembershipArea = (typeof MEMBERSHIP_AREAS)[number];

export function isMembershipArea(value: string): value is MembershipArea {
  return (MEMBERSHIP_AREAS as readonly string[]).includes(value);
}

export interface RolePreset {
  readonly id: RolePresetId;
  /**
   * `tenant` presets cover every module except `platform`; the `platform` preset covers only
   * the `platform` module. No preset mixes the two.
   */
  readonly scope: 'tenant' | 'platform';
  /** `'all'` grants every registered permission in scope; otherwise only these actions. */
  readonly actions: 'all' | readonly string[];
  /** Subjects (`<module>.<resource>`) on which this preset only gets `view`. */
  readonly viewOnlySubjects?: readonly string[];
  /** Subjects on which this preset gets nothing at all. */
  readonly excludedSubjects?: readonly string[];
  /**
   * Permission ids granted on top of the rules above (when registered and in scope): the
   * exceptions of a preset, such as the `member` creating customers.
   */
  readonly grants?: readonly string[];
}

/**
 * Starting points for a role. A preset is resolved against the registry, so a newly
 * installed plugin is covered without editing this file. Custom actions (anything outside
 * view/create/update/delete) are only granted by `admin`; other roles get them explicitly.
 * `manager` only views the organization settings, units and holidays (admin-only subjects),
 * and the automations and AI agents; `member` gets nothing of the management areas
 * (campaigns, automations, AI agents, reports) nor of the service level policies; only `admin`
 * sees the audit log.
 * `platform-admin` is the platform operator (provisions tenants), not a tenant role.
 * `grants` are the exceptions: the manager archives customers, the member creates and edits
 * customers and contacts.
 */
export const ROLE_PRESETS: Readonly<Record<RolePresetId, RolePreset>> = {
  admin: { id: 'admin', scope: 'tenant', actions: 'all' },
  manager: {
    id: 'manager',
    scope: 'tenant',
    actions: ['view', 'create', 'update', 'delete'],
    viewOnlySubjects: MANAGER_VIEW_ONLY_CORE_SUBJECTS,
    excludedSubjects: ADMIN_EXCLUSIVE_CORE_SUBJECTS,
    grants: MANAGER_EXTRA_CORE_PERMISSIONS,
  },
  member: {
    id: 'member',
    scope: 'tenant',
    actions: ['view'],
    excludedSubjects: [
      ...MANAGEMENT_CORE_SUBJECTS,
      ...ADMIN_EXCLUSIVE_CORE_SUBJECTS,
      ...SUPERVISION_CORE_SUBJECTS,
    ],
    grants: MEMBER_EXTRA_CORE_PERMISSIONS,
  },
  'platform-admin': { id: 'platform-admin', scope: 'platform', actions: 'all' },
};

export function isRolePresetId(value: string): value is RolePresetId {
  return (ROLE_PRESET_IDS as readonly string[]).includes(value);
}

/** The permission ids a preset grants with the current registry. */
export function resolvePreset(preset: RolePresetId, registry: PermissionRegistry): PermissionId[] {
  const {
    actions,
    scope,
    viewOnlySubjects = [],
    excludedSubjects = [],
    grants = [],
  } = ROLE_PRESETS[preset];
  return registry
    .all()
    .filter((permission) => (permission.module === PLATFORM_MODULE) === (scope === 'platform'))
    .filter(
      (permission) =>
        grants.includes(permission.id) ||
        (!excludedSubjects.includes(permission.subject) &&
          (actions === 'all' || actions.includes(permission.action)) &&
          (permission.action === 'view' || !viewOnlySubjects.includes(permission.subject))),
    )
    .map((permission) => permission.id);
}

/**
 * What a membership holds: a role preset, the area the member works in and permissions
 * granted to it individually.
 */
export interface MembershipGrant {
  readonly role: RolePresetId;
  /** Where the member works (spec 044); missing reads as `both`. */
  readonly area?: MembershipArea;
  /** Extra permission ids granted on top of the role preset. */
  readonly permissions?: readonly string[];
}

/** Whether a permission belongs to a sales subject (leads, deals, pipelines, capture...). */
export function isSalesPermission(id: PermissionId, registry: PermissionRegistry): boolean {
  const definition = registry.get(id);
  return (
    definition !== undefined &&
    (SALES_CORE_SUBJECTS as readonly string[]).includes(definition.subject)
  );
}

/**
 * The effective permission ids of a membership: the role preset plus the individual grants,
 * minus the sales subjects when the member works in `service` (spec 044) — the area cuts
 * grants too, so no individual grant brings sales back.
 *
 * Fail closed: an individual grant that the registry does not know (a removed plugin, a
 * typo) is dropped, and so is one outside the role scope — a tenant role never receives a
 * `platform.*` permission this way, and `platform-admin` never receives a tenant one.
 */
export function resolveMembershipPermissions(
  grant: MembershipGrant,
  registry: PermissionRegistry,
): PermissionId[] {
  const platformScope = ROLE_PRESETS[grant.role].scope === 'platform';
  const granted = new Set<PermissionId>(resolvePreset(grant.role, registry));
  for (const id of grant.permissions ?? []) {
    const definition = registry.get(id);
    if (definition && (definition.module === PLATFORM_MODULE) === platformScope) {
      granted.add(definition.id);
    }
  }
  if (grant.area === 'service') {
    for (const id of granted) {
      if (isSalesPermission(id, registry)) granted.delete(id);
    }
  }
  return [...granted];
}

/**
 * Extras an administrator gives a `member` on top of the role (L12): named sets of individual
 * grants, stored in the membership's `permissions`. `campaign-author` builds campaigns —
 * broadcasts, journeys, events, lists — without firing them: a broadcast goes to approval
 * (spec 022, rule 12) and a journey waits for whoever has `core.campaign.send` to activate it.
 */
export const MEMBERSHIP_EXTRAS = {
  'campaign-author': ['core.campaign.view', 'core.campaign.create', 'core.campaign.update'],
} as const satisfies Record<string, readonly string[]>;

export type MembershipExtra = keyof typeof MEMBERSHIP_EXTRAS;

export const MEMBERSHIP_EXTRA_IDS = Object.keys(MEMBERSHIP_EXTRAS) as MembershipExtra[];

export function isMembershipExtra(value: string): value is MembershipExtra {
  return Object.hasOwn(MEMBERSHIP_EXTRAS, value);
}

/** The individual grants of a set of extras. */
export function extraPermissions(extras: readonly MembershipExtra[]): string[] {
  return [...new Set(extras.flatMap((extra) => MEMBERSHIP_EXTRAS[extra]))];
}

/** The extras whose grants a membership holds in full. */
export function extrasOf(permissions: readonly string[]): MembershipExtra[] {
  return MEMBERSHIP_EXTRA_IDS.filter((extra) =>
    MEMBERSHIP_EXTRAS[extra].every((id) => permissions.includes(id)),
  );
}
